import React from 'react';
import { Button as FlowbiteButton, Spinner } from 'flowbite-react';

/**
 * Button component - Unified button with MatDash styling
 * Extends Flowbite Button with custom green primary theme
 */
const Button = ({
  children,
  variant = 'primary',
  size = 'md',
  fullWidth = false,
  isLoading = false,
  leftIcon = null,
  rightIcon = null,
  className = '',
  disabled = false,
  ...props
}) => {
  // MatDash button variants with green primary
  const variantClasses = {
    primary: `
      bg-primary text-[#1a1a1a]
      hover:bg-primary-dark hover:shadow-matdash-base
      focus:ring-4 focus:ring-primary/30
      disabled:opacity-50 disabled:cursor-not-allowed
      transition-all duration-200
    `,
    secondary: `
      bg-gray-100 text-gray-900
      hover:bg-gray-200 hover:shadow-matdash-sm
      focus:ring-4 focus:ring-gray-200
      dark:bg-gray-700 dark:text-gray-100 dark:hover:bg-gray-600
      disabled:opacity-50 disabled:cursor-not-allowed
      transition-all duration-200
    `,
    outline: `
      bg-transparent border-2 border-primary text-gray-900
      hover:bg-primary/10 hover:shadow-matdash-sm
      focus:ring-4 focus:ring-primary/30
      dark:text-gray-100 dark:border-primary
      disabled:opacity-50 disabled:cursor-not-allowed
      transition-all duration-200
    `,
    ghost: `
      bg-transparent text-gray-700
      hover:bg-gray-100 hover:text-gray-900
      focus:ring-4 focus:ring-gray-200
      dark:text-gray-300 dark:hover:bg-gray-800 dark:hover:text-gray-100
      disabled:opacity-50 disabled:cursor-not-allowed
      transition-all duration-200
    `,
    danger: `
      bg-red-500 text-white
      hover:bg-red-600 hover:shadow-matdash-base
      focus:ring-4 focus:ring-red-300
      disabled:opacity-50 disabled:cursor-not-allowed
      transition-all duration-200
    `,
    success: `
      bg-green-500 text-white
      hover:bg-green-600 hover:shadow-matdash-base
      focus:ring-4 focus:ring-green-300
      disabled:opacity-50 disabled:cursor-not-allowed
      transition-all duration-200
    `,
  };

  const sizeClasses = {
    xs: 'px-3 py-1.5 text-xs',
    sm: 'px-4 py-2 text-sm',
    md: 'px-5 py-2.5 text-sm',
    lg: 'px-6 py-3 text-base',
    xl: 'px-8 py-3.5 text-base',
  };

  const baseClasses = `
    inline-flex items-center justify-center
    font-medium rounded-lg
    ${variantClasses[variant] || variantClasses.primary}
    ${sizeClasses[size] || sizeClasses.md}
    ${fullWidth ? 'w-full' : ''}
    ${className}
  `;

  return (
    <button
      className={baseClasses}
      disabled={disabled || isLoading}
      {...props}
    >
      {isLoading && (
        <Spinner
          size="sm"
          className="mr-2"
          color={variant === 'primary' ? 'gray' : 'light'}
        />
      )}
      {!isLoading && leftIcon && (
        <span className="mr-2">{leftIcon}</span>
      )}
      {children}
      {!isLoading && rightIcon && (
        <span className="ml-2">{rightIcon}</span>
      )}
    </button>
  );
};

// Button Group Component
export const ButtonGroup = ({ children, className = '' }) => {
  return (
    <div className={`inline-flex rounded-lg shadow-sm ${className}`} role="group">
      {React.Children.map(children, (child, index) => {
        if (!React.isValidElement(child)) return child;

        const isFirst = index === 0;
        const isLast = index === React.Children.count(children) - 1;

        return React.cloneElement(child, {
          className: `
            ${child.props.className || ''}
            ${!isFirst ? 'rounded-l-none border-l-0' : ''}
            ${!isLast ? 'rounded-r-none' : ''}
          `.trim()
        });
      })}
    </div>
  );
};

// Icon Button Variant
export const IconButton = ({
  icon,
  variant = 'ghost',
  size = 'md',
  className = '',
  ...props
}) => {
  const sizeClasses = {
    xs: 'p-1',
    sm: 'p-1.5',
    md: 'p-2',
    lg: 'p-2.5',
    xl: 'p-3',
  };

  return (
    <Button
      variant={variant}
      className={`
        ${sizeClasses[size] || sizeClasses.md}
        !px-0 !py-0 aspect-square
        ${className}
      `}
      {...props}
    >
      {icon}
    </Button>
  );
};

// Floating Action Button
export const FloatingActionButton = ({
  icon,
  position = 'bottom-right',
  className = '',
  ...props
}) => {
  const positionClasses = {
    'bottom-right': 'fixed bottom-6 right-6',
    'bottom-left': 'fixed bottom-6 left-6',
    'top-right': 'fixed top-6 right-6',
    'top-left': 'fixed top-6 left-6',
  };

  return (
    <Button
      variant="primary"
      className={`
        ${positionClasses[position] || positionClasses['bottom-right']}
        !rounded-full p-4 shadow-matdash-lg
        hover:shadow-matdash-xl z-50
        ${className}
      `}
      {...props}
    >
      {icon}
    </Button>
  );
};

export default Button;